// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { AuthnMethod, AuthnViews } from '@imprivata-cloud/authn';
import { ScanningElement } from '@imprivata-cloud/components';

import PasswordHex from '@/assets/passwordHex.svg?react';
import PhoneHex from '@/assets/phone.svg?react';
import PinHex from '@/assets/pin-hex.svg?react';
import ProxCardHex from '@/assets/prox-card-hex.svg?react';
import TempcodeHex from '@/assets/tempcodeHex.svg?react';

// Must stay total: the headers default an absent glyph to the face art, so an
// omitted factor renders a face scan rather than nothing. Use null for a
// deliberately empty hexagon — the default applies only to undefined.
export const FACTOR_GLYPHS: Record<AuthnMethod, React.ReactNode> = {
  [AuthnMethod.FACE_ID]: <ScanningElement />,
  [AuthnMethod.PROX]: <ProxCardHex />,
  [AuthnMethod.PASSWORD]: <PasswordHex />,
  [AuthnMethod.IMPR_ID]: <PhoneHex />,
  [AuthnMethod.IMPR_PIN]: <PinHex />,
  [AuthnMethod.IID_DISABLED]: <PhoneHex />,
  [AuthnMethod.TEMP_CODE]: <TempcodeHex />,
  [AuthnMethod.EMAIL_OTP]: <TempcodeHex />,
};

// Destructuring an EventPayload drops the correlation between `event` and
// `data`, so a factor payload has to be re-narrowed at the read site.
export const isAuthnMethod = (data: unknown): data is AuthnMethod =>
  Object.values(AuthnMethod).some(method => method === data);

// Total, like FACTOR_GLYPHS and for the same reason: an omitted screen falls
// through to the headers' face default, so a new AuthnViews member has to be a
// compile error here rather than a face glyph on an unrelated screen.
export const SCREEN_GLYPHS: Record<AuthnViews, React.ReactNode | undefined> = {
  [AuthnViews.FACE_ID]: <ScanningElement />,
  [AuthnViews.INFORMATION_DIALOG]: <ScanningElement />,
  [AuthnViews.PASSWORD_FORM]: FACTOR_GLYPHS[AuthnMethod.PASSWORD],
  // Recovery is not a factor capture and has no glyph of its own; undefined
  // defers to the header default rather than asserting one.
  [AuthnViews.ACCOUNT_RECOVERY]: undefined,
  [AuthnViews.ENROLL_PROMPT]: <ScanningElement />,
  [AuthnViews.ENROLL_INTRO]: <ScanningElement />,
  [AuthnViews.RECONSENT_INTRO]: <ScanningElement />,
};

// The gates' consent and loader steps come from @imprivata-cloud/consent, which
// emits no view of its own, so they still arrive unnamed mid-gate and take the
// face glyph their flow implies.
export const gateGlyphOf = (screen?: AuthnViews): React.ReactNode =>
  (screen && SCREEN_GLYPHS[screen]) ?? <ScanningElement />;
